import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronLeftIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { supportAPI, getErrorMessage } from "../services/api";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  open: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
};

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    supportAPI.getTicket(id).then((res) => setTicket(res.data)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await supportAPI.addMessage(id!, { message: message.trim() });
      const res = await supportAPI.getTicket(id!);
      setTicket(res.data);
      setMessage("");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <LoadingSpinner className="py-20" size="lg" />;
  if (!ticket) return <div className="text-center py-20 text-gray-500">Ticket not found</div>;

  return (
    <>
      <Helmet><title>Ticket #{ticket.ticket_number} – Kushi Fashions</title></Helmet>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/support")} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-6 text-sm">
          <ChevronLeftIcon className="h-4 w-4" /> Support
        </button>

        <div className="card p-5 mb-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs text-gray-400 mb-1">#{ticket.ticket_number} · {ticket.category}</p>
              <h1 className="text-lg font-bold text-gray-900">{ticket.subject}</h1>
            </div>
            <span className={`badge ${STATUS_COLORS[ticket.status] || "bg-gray-100 text-gray-600"}`}>
              {ticket.status.replace("_", " ")}
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="card p-5 mb-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {ticket.messages?.map((msg: any) => (
            <div
              key={msg.id}
              className={`flex ${msg.is_admin ? "justify-start" : "justify-end"}`}
            >
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.is_admin
                  ? msg.is_ai_response
                    ? "bg-purple-50 text-purple-900 rounded-bl-sm"
                    : "bg-gray-100 text-gray-800 rounded-bl-sm"
                  : "bg-primary-600 text-white rounded-br-sm"
              }`}>
                <p className="text-xs font-medium mb-1 opacity-70">
                  {msg.is_ai_response ? "🤖 AI Assistant" : msg.sender_name}
                </p>
                <p className="text-sm">{msg.message}</p>
                <p className="text-xs opacity-50 mt-1">{format(new Date(msg.created_at), "dd MMM, h:mm a")}</p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply form */}
        {ticket.status !== "closed" && (
          <form onSubmit={handleSend} className="flex gap-3">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your reply..."
              className="flex-1 input-field"
            />
            <button type="submit" disabled={sending || !message.trim()} className="btn-primary px-4 py-2.5 flex items-center gap-2">
              <PaperAirplaneIcon className="h-5 w-5" />
              Send
            </button>
          </form>
        )}
        {ticket.status === "closed" && (
          <div className="text-center text-sm text-gray-400 p-4">This ticket is closed.</div>
        )}
      </div>
    </>
  );
}
